/**
 * src/utils/webpush.ts
 * Cloudflare Native Web Push Client
 * Replaces Firebase FCM with W3C standard Push API (VAPID)
 * Zero external dependencies, 100% free, runs directly on Cloudflare Pages and modern browsers
 */

// Permanent VAPID public key for CKAudit
export const VAPID_PUBLIC_KEY = 'BK4Ust582KPaBl9CubsHzycMLHaw_BwrQB8amG6mNSfPsFYaZQhdv5BSLu9OZRxx8wgJzWoW2xJIIWORpDj1OnM';

/**
 * Convert base64 string to Uint8Array for applicationServerKey
 */
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return buffer;
}

/**
 * Check if Web Push is supported in the current environment
 */
export function isPushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Request notification permissions and register Web Push subscription with Cloudflare edge API
 */
export async function requestNotificationPermission(): Promise<{ permission: NotificationPermission; token: string | null }> {
  if (!isPushSupported()) {
    return {
      permission: typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'denied',
      token: null
    };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { permission, token: null };
    }

    // Register PWA service worker
    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    // Check existing subscription or subscribe new
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    }

    const tokenJson = JSON.stringify(subscription.toJSON());

    // Register token with Cloudflare Pages Functions edge API
    const jwtToken = localStorage.getItem('ckaudit_jwt');
    if (jwtToken) {
      await fetch('/api/notifications/register-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({
          token: tokenJson,
          device_info: navigator.userAgent
        })
      }).catch((err) => console.warn('[WebPush Edge Register] Failed to sync token:', err));
    }

    return { permission: 'granted', token: tokenJson };
  } catch (error: any) {
    console.warn('[WebPush Client] Error enabling push notifications:', error);
    return {
      permission: typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'denied',
      token: null
    };
  }
}

/**
 * Listen for foreground push notifications forwarded by the service worker
 */
export async function onForegroundMessage(callback: (payload: any) => void): Promise<(() => void) | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  const handler = (event: MessageEvent) => {
    if (event.data && event.data.type === 'PUSH_NOTIFICATION_RECEIVED') {
      callback(event.data.payload);
    }
  };

  navigator.serviceWorker.addEventListener('message', handler);

  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
  };
}
